/**
 * Route-follow nav maths — the pure core behind activating a route and steering it.
 *
 * Everything a followed leg needs, worked out from the boat's live position and a
 * stored active-leg index: range and bearing to the next waypoint, the signed
 * cross-track error (which tells us which way to steer back), how far is left of
 * the whole route, and the decision of when a waypoint is reached so the active
 * leg rolls on to the next one.
 *
 * Kept pure and clock-free (no store, no map, no React) so the geometry is easy to
 * test hard and safe to run on a background fix. The store owns the active-leg
 * index; this module only ever reasons about "given the boat is here and we're on
 * leg N, what now?".
 *
 * Sign convention for cross-track, inherited from distanceToRun (see fix.ts):
 *   xteNm > 0  → we are to the RIGHT of the track, so steer LEFT to close it.
 *   xteNm < 0  → we are to the LEFT of the track, so steer RIGHT.
 * That's the same "+ = right of track" the XTE data box and the off-track alarm
 * already use, so the HUD arrow, the data field and the alarm never disagree.
 *
 * Positions are [lon, lat] WGS84 degrees; distances are nautical miles; bearings
 * are degrees true. Rhumb lines throughout, matching the rest of the chartwork
 * (that's what students plot on paper).
 */
import { distanceToRun, rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import { DEFAULT_ARRIVAL_RADIUS_NM, type Route, type Waypoint } from './route';

/** Which way to steer to get back onto the leg line, or 'on' when we're on it. */
export type SteerSide = 'left' | 'right' | 'on';

/**
 * Inside this cross-track distance we call it "on track" rather than nagging a
 * steer side for the last few metres of wander. ~18 m, a touch under a boat
 * length, so the arrow settles instead of flickering left/right on the line.
 */
export const STEER_DEADBAND_NM = 0.01;

export interface FollowLeg {
  /** The active leg (0-based): the boat is steering waypoint[legIndex] → [legIndex+1]. */
  legIndex: number;
  fromId: string;
  toId: string;
  from: [number, number];
  to: [number, number];
  /** Label for the waypoint we're making for (its name, or its 1-based number). */
  targetName: string;
  /** Direct distance from the boat to the next waypoint, nm (the range we read). */
  rangeNm: number;
  /** Bearing from the boat to the next waypoint, degrees true. */
  bearingDeg: number;
  /** Bearing along the leg line itself (from → to), degrees true. */
  legBearingDeg: number;
  /** Signed cross-track error, nm. + = right of track, − = left. */
  xteNm: number;
  /** Which way to steer to get back on the leg line. */
  steer: SteerSide;
  /** Along-track distance from the foot of the perpendicular to the leg end, nm.
   *  Goes to zero and then negative once we pass the waypoint's abeam line. */
  distanceToGoNm: number;
  /** Distance still to run to the end of the whole route, nm. */
  remainingNm: number;
}

/** Number of legs in a route (waypoints − 1, never negative). */
export function legCount(route: Route): number {
  return Math.max(0, route.waypoints.length - 1);
}

/**
 * The APPROACH leg: steering from wherever we are now to the FIRST waypoint.
 *
 * Leg n runs waypoint[n] to waypoint[n+1], so leg 0 makes for WP2 and WP1 is only
 * ever a leg's starting point. That leaves no way to say "take me to WP1", which is
 * exactly what we want when we pick a route up from somewhere else, or start it
 * from a mooring a mile short of the first mark. This index sits one below leg 0
 * and means that approach; stepping on from it lands on leg 0, which is the leg out
 * of the waypoint we have just reached.
 */
export const APPROACH_LEG_INDEX = -1;

/**
 * Pin a leg index into the valid range for a route, so a stale index left over
 * from an edit/reverse/delete can never point past the end. A route with no legs
 * clamps to 0 (callers still check legCount before steering it).
 */
export function clampLegIndex(route: Route, i: number): number {
  const n = legCount(route);
  if (n <= 0) return 0;
  if (!Number.isFinite(i)) return 0;
  return Math.max(0, Math.min(Math.trunc(i), n - 1));
}

/**
 * The same clamp for the FOLLOW index, which has one more valid value: the
 * approach leg to WP1 sitting below leg 0. Anything that only understands real
 * legs keeps using clampLegIndex, which pulls the approach up to leg 0.
 */
export function clampFollowLegIndex(route: Route, i: number): number {
  const n = legCount(route);
  if (n <= 0) return 0;
  if (!Number.isFinite(i)) return 0;
  return Math.max(APPROACH_LEG_INDEX, Math.min(Math.trunc(i), n - 1));
}

/**
 * The leg from the boat to the first waypoint, in the same shape a real leg comes
 * back in so the HUD, the data fields and the chart line all read it the same way.
 *
 * There is no planned track here, because the track is drawn from wherever we
 * happen to be to the mark, and it is redrawn from our new position on every fix.
 * So cross-track is zero by construction rather than by luck: there is nothing to
 * be off. Distance to run covers the approach plus every leg of the route after it,
 * so the remaining-distance readout means the same thing it always did.
 */
export function approachLeg(route: Route, boat: [number, number]): FollowLeg | null {
  const first = route.waypoints[0];
  if (!first) return null;
  const dist = rhumbDistanceNm(boat, first.position);
  const bearing = rhumbBearingDeg(boat, first.position);
  let remaining = dist;
  for (let j = 0; j + 1 < route.waypoints.length; j++) {
    remaining += rhumbDistanceNm(route.waypoints[j].position, route.waypoints[j + 1].position);
  }
  return {
    legIndex: APPROACH_LEG_INDEX,
    fromId: first.id,
    toId: first.id,
    from: boat,
    to: first.position,
    targetName: waypointLabel(first.name, 0),
    rangeNm: dist,
    bearingDeg: bearing,
    legBearingDeg: bearing,
    xteNm: 0,
    steer: 'on',
    distanceToGoNm: dist,
    remainingNm: remaining,
  };
}

/**
 * Turn a signed cross-track error into the way to steer. + = right of track, so
 * we steer left to close it; − = left of track, steer right. Within the deadband
 * we're on the line, so no nag.
 */
export function steerSide(xteNm: number, deadbandNm = STEER_DEADBAND_NM): SteerSide {
  if (!(Math.abs(xteNm) > deadbandNm)) return 'on';
  return xteNm > 0 ? 'left' : 'right';
}

/**
 * The arrival radius to use when deciding the boat has reached a waypoint: the
 * point's own radius, else the route-wide one, else a sensible default. This is
 * the reaching-the-waypoint geometry, deliberately separate from whether the
 * audible arrival ALARM is armed — we still want to advance the leg at an unarmed
 * waypoint, we just do it quietly.
 */
export function arrivalRadiusForLeg(route: Route, toWp: Waypoint): number {
  const r = toWp.arrivalRadiusNm ?? route.arrivalRadiusNm ?? DEFAULT_ARRIVAL_RADIUS_NM;
  return r > 0 ? r : DEFAULT_ARRIVAL_RADIUS_NM;
}

/** Waypoint label: its own name, or the 1-based sequence number when unnamed. */
function waypointLabel(name: string | undefined, index: number): string {
  const n = name?.trim();
  return n ? n : String(index + 1);
}

/**
 * The live state of the active leg, computed from the boat's position. Returns
 * null when the index doesn't name a real leg (too few waypoints, or a stale
 * index out of range) so callers show "waiting" rather than a NaN.
 */
export function followLeg(
  route: Route,
  boat: [number, number],
  legIndex: number,
): FollowLeg | null {
  const n = legCount(route);
  if (n <= 0 || legIndex < 0 || legIndex >= n) return null;
  const a = route.waypoints[legIndex];
  const b = route.waypoints[legIndex + 1];
  const dtr = distanceToRun(boat, a.position, b.position);

  // Distance still to run to the route end: the along-track distance left on this
  // leg, plus every whole leg after it.
  let remaining = dtr.distanceToGoNm;
  for (let j = legIndex + 1; j < n; j++) {
    remaining += rhumbDistanceNm(route.waypoints[j].position, route.waypoints[j + 1].position);
  }
  if (remaining < 0) remaining = 0;

  return {
    legIndex,
    fromId: a.id,
    toId: b.id,
    from: a.position,
    to: b.position,
    targetName: waypointLabel(b.name, legIndex + 1),
    rangeNm: rhumbDistanceNm(boat, b.position),
    bearingDeg: rhumbBearingDeg(boat, b.position),
    legBearingDeg: rhumbBearingDeg(a.position, b.position),
    xteNm: dtr.xteNm,
    steer: steerSide(dtr.xteNm),
    distanceToGoNm: dtr.distanceToGoNm,
    remainingNm: remaining,
  };
}

/**
 * Has the boat reached the waypoint at the end of this leg? Two ways count, so we
 * never get stuck circling a point we didn't quite clip:
 *   1. Inside the waypoint's arrival radius (the clean case).
 *   2. Past its abeam line — the foot of the perpendicular has reached the leg
 *      end (distanceToGo ≤ 0) — which catches a waypoint passed wide of the
 *      radius. That's the closest-approach/passing rule the brief asks for.
 */
export function hasArrived(route: Route, boat: [number, number], legIndex: number): boolean {
  const n = legCount(route);
  if (legIndex < 0 || legIndex >= n) return false;
  const a = route.waypoints[legIndex];
  const b = route.waypoints[legIndex + 1];
  if (rhumbDistanceNm(boat, b.position) <= arrivalRadiusForLeg(route, b)) return true;
  const dtr = distanceToRun(boat, a.position, b.position);
  return dtr.distanceToGoNm <= 0;
}

/**
 * Have we reached WP1 on the approach leg? Only the arrival radius counts here.
 * The abeam test that catches a waypoint passed wide needs a planned track to take
 * the perpendicular from, and the approach has none: its line is redrawn from our
 * own position on every fix, so the foot of the perpendicular is always us.
 */
export function approachArrived(route: Route, boat: [number, number]): boolean {
  const first = route.waypoints[0];
  if (!first) return false;
  return rhumbDistanceNm(boat, first.position) <= arrivalRadiusForLeg(route, first);
}

export interface AdvanceResult {
  /** True the moment the active waypoint counts as reached. */
  arrived: boolean;
  /** True when the waypoint reached is the LAST one, so follow mode ends. */
  complete: boolean;
  /** The leg to steer next once arrived, or null when complete (or not arrived). */
  nextLegIndex: number | null;
}

/**
 * Decide what happens now the boat is at (or short of) the active waypoint:
 * nothing yet, roll on to the next leg, or finish the route at the final point.
 * Pure — the store/runner applies the result; this only reads geometry.
 */
export function advanceDecision(
  route: Route,
  boat: [number, number],
  legIndex: number,
): AdvanceResult {
  const n = legCount(route);
  if (legIndex < 0 || legIndex >= n) {
    return { arrived: false, complete: false, nextLegIndex: null };
  }
  if (!hasArrived(route, boat, legIndex)) {
    return { arrived: false, complete: false, nextLegIndex: null };
  }
  // Reached waypoint[legIndex + 1]. If that was the last waypoint, we're done.
  if (legIndex >= n - 1) {
    return { arrived: true, complete: true, nextLegIndex: null };
  }
  return { arrived: true, complete: false, nextLegIndex: legIndex + 1 };
}
